import { Component, input, output, inject, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '@joyinvoice/web/shared/data-access';
import { PermissionsService } from '@joyinvoice/web/shared/data-access';

import { TranslatePipe } from '@ngx-translate/core';
import { SharedPrimeNgModule } from '../shared-prime-ng.module';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [SharedPrimeNgModule, CommonModule, RouterModule, RouterLink, RouterLinkActive, TranslatePipe],
  template: `
    <aside class="sidebar" [class.collapsed]="collapsed()" [class.mobile-open]="!collapsed()">
      <!-- Logo -->
      <div class="sidebar-header">
        <div class="logo">
          <span class="logo-icon">⚡</span>
          @if (!collapsed()) {
            <span class="logo-text">{{ 'SIDEBAR.APP_NAME' | translate }}</span>
          }
        </div>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        @for (item of visibleNavItems(); track item.route) {
          @if (item.dividerBefore) {
            <div class="nav-divider"></div>
          }
          <a
            class="nav-item"
            [routerLink]="item.route"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
            [pTooltip]="'SIDEBAR.MENU.' + item.label | translate"
            tooltipPosition="right"
            [tooltipDisabled]="!collapsed()"
          >
            <i class="pi" [ngClass]="item.icon"></i>
            @if (!collapsed()) {
              <span class="nav-label">{{ 'SIDEBAR.MENU.' + item.label | translate }}</span>
            }
          </a>
        }
      </nav>

      <!-- Bottom section -->
      <div class="sidebar-footer">
        @if (!collapsed()) {
          <div class="plan-badge">
            <i class="pi pi-star-fill"></i>
            <div class="plan-info">
              <span class="plan-name">{{ auth.currentTenant()?.status === 'trial' ? ('SIDEBAR.TRIAL_PLAN' | translate) : ('SIDEBAR.PRO_PLAN' | translate) }}</span>
              <span class="plan-cta">{{ 'SIDEBAR.UPGRADE' | translate }}</span>
            </div>
          </div>
        }
      </div>
    </aside>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .sidebar {
      width: 260px;
      height: 100vh;
      background: var(--bg-card);
      border-right: 1px solid var(--border);
      display: flex;
      flex-direction: column;
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 100;
      flex-shrink: 0;
    }
    .sidebar.collapsed {
      width: 72px;
    }
    .sidebar-header {
      padding: 1.25rem 1rem;
      border-bottom: 1px solid var(--border);
    }
    .logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    .logo-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, var(--primary-500), var(--primary-700));
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
      flex-shrink: 0;
    }
    .logo-text {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--text-primary);
      white-space: nowrap;
    }
    .sidebar-nav {
      flex: 1;
      padding: 0.75rem 0.5rem;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .nav-divider {
      height: 1px;
      background: var(--border);
      margin: 0.5rem 0.75rem;
      opacity: 0.5;
    }
    .nav-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.7rem 0.85rem;
      border-radius: 8px;
      color: var(--text-secondary);
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 500;
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    .nav-item:hover {
      background: var(--bg-secondary);
      color: var(--text-primary);
    }
    .nav-item.active {
      background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(99, 102, 241, 0.05));
      color: var(--primary-500);
      font-weight: 600;
    }
    .nav-item.active i {
      color: var(--primary-500);
    }
    .nav-item i {
      font-size: 1.1rem;
      width: 22px;
      text-align: center;
      flex-shrink: 0;
    }
    .sidebar-footer {
      padding: 1rem;
      border-top: 1px solid var(--border);
    }
    .plan-badge {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
      border-radius: 10px;
      border: 1px solid rgba(99, 102, 241, 0.2);
    }
    .plan-badge i {
      color: var(--primary-400);
      font-size: 1.25rem;
    }
    .plan-info {
      display: flex;
      flex-direction: column;
    }
    .plan-name {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--text-primary);
    }
    .plan-cta {
      font-size: 0.7rem;
      color: var(--primary-400);
      cursor: pointer;
    }

    @media (max-width: 768px) {
      .sidebar {
        position: fixed;
        left: -280px;
        transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: none;
      }
      .sidebar.mobile-open {
        left: 0;
        box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
      }
      .sidebar.collapsed {
        width: 260px;
        left: -280px;
      }
    }
  `],
})
export class SidebarComponent {
  collapsed = input(false);
  isMobile = input(false);
  toggleCollapse = output<void>();
  
  auth = inject(AuthService);
  private readonly permissions = inject(PermissionsService);

  visibleNavItems = computed(() => this.permissions.getVisibleNavItems());
}

